import { Injectable } from '@angular/core';
import { get, set, del, keys } from 'idb-keyval';
import {
  HEALTH_INDICATOR_TTL_MS,
  TTL_MS,
} from '../shared/constants/chart.constants';
import { ChartData } from 'chart.js';
import { TrendPoint } from '../models/trend-chart.model';

export interface TrendCacheEntry {
  rawData: TrendPoint[];
  fetchedAt: number;
}

function k(uid: string, range: string) {
  return `trend::${uid}::${range}`;
}

function kHealthIndicator(uid: string, range: string) {
  return `healthIndicator::${uid}::${range}`;
}

function kAlarmDetails(uid: string, range: string) {
  return `alarmDetails::${uid}::${range}`;
}

function roughSize(obj: unknown): number {
  try {
    return new Blob([JSON.stringify(obj)]).size;
  } catch {
    return 0;
  }
}

@Injectable({
  providedIn: 'root',
})
export class TrendCacheService {
  constructor() {}

  async get(uid: string, range: string): Promise<TrendCacheEntry | null> {
    const entry = await get<TrendCacheEntry>(k(uid, range));
    if (!entry) return null;
    const fresh = Date.now() - entry.fetchedAt < TTL_MS;
    return fresh ? entry : null; // return null to force refresh when stale
  }

  async getStale(uid: string, range: string): Promise<TrendCacheEntry | null> {
    return (await get<TrendCacheEntry>(k(uid, range))) ?? null;
  }

  async set(uid: string, range: string, entry: TrendCacheEntry) {
    if (roughSize(entry) > 50 * 1024 * 1024) return; // 50MB guard
    await set(k(uid, range), entry);
  }

  async clear(uid: string, range: string) {
    await del(k(uid, range));
  }

  async pruneExpired() {
    const allKeys = await keys();
    const now = Date.now();
    await Promise.all(
      allKeys
        .filter((x: any) => typeof x === 'string' && x.startsWith('trend::'))
        .map(async (kk) => {
          const v = await get<TrendCacheEntry>(kk);
          if (!v?.fetchedAt || now - v.fetchedAt >= TTL_MS) await del(kk);
        }),
    );
  }

  async getHealthIndicator(
    uid: string,
    range: string,
  ): Promise<TrendCacheEntry | null> {
    const entry = await get<TrendCacheEntry>(kHealthIndicator(uid, range));
    if (!entry) return null;
    const fresh = Date.now() - entry.fetchedAt < HEALTH_INDICATOR_TTL_MS;
    return fresh ? entry : null;
  }

  async setHealthIndicator(uid: string, range: string, entry: TrendCacheEntry) {
    if (roughSize(entry) > 50 * 1024 * 1024) return;
    await set(kHealthIndicator(uid, range), entry);
  }

  async clearHealthIndicator(uid: string, range: string) {
    await del(kHealthIndicator(uid, range));
  }

  async getAlarmDetails(
    uid: string,
    range: string,
  ): Promise<TrendCacheEntry | null> {
    const entry = await get<TrendCacheEntry>(kAlarmDetails(uid, range));
    if (!entry) return null;
    const fresh = Date.now() - entry.fetchedAt < TTL_MS;
    return fresh ? entry : null;
  }

  async setAlarmDetails(uid: string, range: string, entry: TrendCacheEntry) {
    if (roughSize(entry) > 50 * 1024 * 1024) return;
    await set(kAlarmDetails(uid, range), entry);
  }

  async clearAlarmDetails(uid: string, range: string) {
    await del(kAlarmDetails(uid, range));
  }
}
